import { MapUtils } from 'record-it-utils/utils/MapUtils/MapUtils';
import { AnyCommonSynchronizationEnvironmentTypesConstraint } from '../../types';
import { ObjectFieldInfo, ObjectFieldInfos, EntityInfo } from '../EntityInfo/EntityInfo';

/**
 * A tree representation of field infos.
 * This is used, so paths can be easily accessed or iterated over for e.g. validating an entity
 */
export class FieldInfoHandler<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint, TEntity> {
  public static createForEntityInfo<TStaticCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint, TStaticEntity>(entityInfo: EntityInfo<TStaticCommonSynchronizationEnvironmentTypes, any, TStaticEntity>): FieldInfoHandler<TStaticCommonSynchronizationEnvironmentTypes, TStaticEntity> {
    return new FieldInfoHandler<TStaticCommonSynchronizationEnvironmentTypes, TStaticEntity>({
      entityInfo,
      fieldInfos: entityInfo.fieldInfos,
      fieldInfoConfigurations: new Map()
    });
  }
  private readonly entityInfo: EntityInfo<TCommonSynchronizationEnvironmentTypes, any, TEntity>;
  private readonly fieldInfos: ObjectFieldInfos<TCommonSynchronizationEnvironmentTypes, any>;
  private readonly fieldInfoConfigurations: FieldInfoConfigurationsMap<TCommonSynchronizationEnvironmentTypes>;
  private fieldInfosCache: WeakMap<ObjectFieldInfos<any, any>, Array<ObjectFieldInfo<any, any, any>>> = new WeakMap();
  private constructor({
    entityInfo,
    fieldInfos,
    fieldInfoConfigurations
  }: ConstructorOptions<TCommonSynchronizationEnvironmentTypes, TEntity>) {
    this.entityInfo = entityInfo;
    this.fieldInfos = fieldInfos;
    this.fieldInfoConfigurations = fieldInfoConfigurations;
  }
  public getAllFieldInfos(): Array<FieldInfoWithPath<TCommonSynchronizationEnvironmentTypes>> {
    const fieldInfos: Array<FieldInfoWithPath<TCommonSynchronizationEnvironmentTypes>> = [];
    this.forEachFieldInfo(fieldInfo => {
      fieldInfos.push(fieldInfo);
    });
    return fieldInfos;
  }
  public getAllFieldInfosOfData({
    data
  }: {
    data: Record<string | number | symbol, any>;
  }): Array<FieldInfoOfData<TCommonSynchronizationEnvironmentTypes>> {
    const fieldInfos: Array<FieldInfoOfData<TCommonSynchronizationEnvironmentTypes>> = [];
    this.forEachFieldInfoOfData({
      data,
      callback: fieldInfo => {
        fieldInfos.push(fieldInfo);
      }
    });
    return fieldInfos;
  }
  public forEachFieldInfo(callback: ForEachFieldInfoCallback<TCommonSynchronizationEnvironmentTypes>): void {
    this.forEachFieldInfoRecursive({
      path: [],
      fieldInfos: this.fieldInfos,
      callback
    });
  }

  /**
   * the callback only get's called for existing paths
   */
  public forEachFieldInfoOfData({
    data,
    callback
  }: ForEachFieldInfoOfDataOptions<TCommonSynchronizationEnvironmentTypes>): void {
    this.forEachFieldInfoOfDataRecursive({
      data,
      path: [],
      fieldInfos: this.fieldInfos,
      callback
    });
  }
  public filterInfos(callback: FilterInfosCallback<TCommonSynchronizationEnvironmentTypes>): FieldInfoHandler<TCommonSynchronizationEnvironmentTypes, TEntity> {
    const filteredInfos = this.filterInfosRecursive({
      fieldInfos: this.fieldInfos,
      filterCallback: ({
        fieldInfo
      }) => {
        if (this.fieldInfoConfigurations.get(fieldInfo)?.hidden) {
          return false;
        }
        return callback({
          fieldInfo
        });
      }
    });
    const {
      fieldInfos,
      fieldInfoConfigurations
    } = this.generateFieldInfosFromFilteredInfos(filteredInfos);
    return new FieldInfoHandler({
      entityInfo: this.entityInfo,
      fieldInfos,
      fieldInfoConfigurations
    });
  }
  private forEachFieldInfoRecursive<TObject>({
    path,
    fieldInfos,
    callback
  }: {
    path: FieldInfoPath;
    fieldInfos: ObjectFieldInfos<TCommonSynchronizationEnvironmentTypes, TObject>;
    callback: ForEachFieldInfoCallback<TCommonSynchronizationEnvironmentTypes>;
  }): void {
    const arrayFieldInfos = this.getCachedArrayFieldInfos(fieldInfos);
    for (const info of arrayFieldInfos) {
      const currentPath = [...path, info.name as string];
      if (!this.fieldInfoConfigurations.get(info as any)?.hidden) {
        callback({
          path: currentPath,
          fieldInfo: info as any
        });
      }
      const {
        innerInfo
      } = info;
      if (innerInfo) {
        this.forEachFieldInfoRecursive({
          path: currentPath,
          fieldInfos: Array.isArray(innerInfo) ? innerInfo[0] : innerInfo,
          callback
        });
      }
    }
  }
  private forEachFieldInfoOfDataRecursive<TObject extends Record<string | number | symbol, any>>({
    data,
    path,
    fieldInfos,
    callback
  }: {
    data: TObject;
    path: Array<string | number | symbol>;
    fieldInfos: ObjectFieldInfos<TCommonSynchronizationEnvironmentTypes, TObject>;
    callback: ForEachFieldInfoOfDataCallback<TCommonSynchronizationEnvironmentTypes>;
  }): void {
    const arrayFieldInfos = this.getCachedArrayFieldInfos(fieldInfos);
    for (const info of arrayFieldInfos) {
      const currentPath = [...path, info.name];
      callback({
        path: currentPath,
        data,
        value: data[info.name],
        fieldInfo: info as any
      });
      const {
        innerInfo
      } = info;
      const value = data[info.name];
      if (value == null || !innerInfo) {
        continue;
      }
      if (Array.isArray(innerInfo)) {
        if (!Array.isArray(value)) {
          throw new Error(`entity of type "${this.entityInfo.entityName}" should have an array at "${currentPath.join('.')}", but it's some kind of other value"`);
        }
        for (const [index, item] of value.entries()) {
          this.forEachFieldInfoOfDataRecursive({
            data: item,
            path: [...currentPath, index],
            fieldInfos: innerInfo[0],
            callback
          });
        }
      } else {
        this.forEachFieldInfoOfDataRecursive({
          data: value,
          path: currentPath,
          fieldInfos: innerInfo,
          callback
        });
      }
    }
  }
  private filterInfosRecursive({
    fieldInfos,
    filterCallback
  }: {
    fieldInfos: ObjectFieldInfos<TCommonSynchronizationEnvironmentTypes, any>;
    filterCallback: FilterInfosCallback<TCommonSynchronizationEnvironmentTypes>;
  }): Array<FilteredEntityInfo<TCommonSynchronizationEnvironmentTypes>> {
    const filteredInfos: Array<FilteredEntityInfo<TCommonSynchronizationEnvironmentTypes>> = [];
    const arrayFieldInfos = this.getCachedArrayFieldInfos(fieldInfos);
    for (const info of arrayFieldInfos) {
      const {
        innerInfo
      } = info;
      const innerInfos = innerInfo ? this.filterInfosRecursive({
        fieldInfos: Array.isArray(innerInfo) ? innerInfo[0] : innerInfo,
        filterCallback
      }) : [];
      const infoFilterResult = filterCallback({
        fieldInfo: info
      });
      if (infoFilterResult || innerInfos.length) {
        filteredInfos.push({
          info,
          innerInfos,
          isHidden: !infoFilterResult,
          isArray: Array.isArray(innerInfo)
        });
      }
    }
    return filteredInfos;
  }
  private generateFieldInfosFromFilteredInfos(filteredInfos: Array<FilteredEntityInfo<TCommonSynchronizationEnvironmentTypes>>): {
    fieldInfos: ObjectFieldInfos<TCommonSynchronizationEnvironmentTypes, any>;
    fieldInfoConfigurations: FieldInfoConfigurationsMap<TCommonSynchronizationEnvironmentTypes>;
  } {
    const fieldInfoConfigurations: FieldInfoConfigurationsMap<TCommonSynchronizationEnvironmentTypes> = new Map();
    const fieldInfos: ObjectFieldInfos<TCommonSynchronizationEnvironmentTypes, any> = {};
    for (const filteredInfo of filteredInfos) {
      const innerFieldInfosResult = filteredInfo.innerInfos.length ? this.generateFieldInfosFromFilteredInfos(filteredInfo.innerInfos) : null;
      let innerInfo: ObjectFieldInfo<TCommonSynchronizationEnvironmentTypes, any, any>['innerInfo'] = null;
      if (innerFieldInfosResult) {
        innerInfo = filteredInfo.isArray ? [innerFieldInfosResult.fieldInfos] : innerFieldInfosResult.fieldInfos;
        MapUtils.mergeMapsInPlace(fieldInfoConfigurations, innerFieldInfosResult.fieldInfoConfigurations);
      }
      const fieldInfo = {
        ...filteredInfo.info,
        innerInfo
      };
      if (filteredInfo.isHidden) {
        fieldInfoConfigurations.set(fieldInfo, {
          hidden: true
        });
      }
      fieldInfos[filteredInfo.info.name] = fieldInfo;
    }
    return {
      fieldInfos,
      fieldInfoConfigurations
    };
  }
  private getCachedArrayFieldInfos<TObject>(fieldInfos: ObjectFieldInfos<TCommonSynchronizationEnvironmentTypes, TObject>): Array<ObjectFieldInfo<TCommonSynchronizationEnvironmentTypes, TObject, keyof TObject>> {
    let arrayFieldInfos = this.fieldInfosCache.get(fieldInfos);
    if (!arrayFieldInfos) {
      arrayFieldInfos = Object.values(fieldInfos);
      this.fieldInfosCache.set(fieldInfos, arrayFieldInfos);
    }
    return arrayFieldInfos as Array<ObjectFieldInfo<TCommonSynchronizationEnvironmentTypes, TObject, keyof TObject>>;
  }
}

/**
 * A path which also contains the Array indices.
 * e.g. ['arrayOfObjects', 1, 'name']
 */
export type FieldInfoDetailedPath = Array<string | number | symbol>;

/**
 * A path which doesn't contain the Array indices.
 * e.g. ['arrayOfObjects', 'name']
 */
export type FieldInfoPath = Array<string>;
export type ForEachFieldInfoCallback<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint> = (options: FieldInfoWithPath<TCommonSynchronizationEnvironmentTypes>) => void;
export type ForEachFieldInfoOfDataOptions<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint> = {
  data: Record<string | number | symbol, any>;
  callback: ForEachFieldInfoOfDataCallback<TCommonSynchronizationEnvironmentTypes>;
};
export type ForEachFieldInfoOfDataCallback<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint> = (options: ForEachFieldInfoOfDataCallbackOptions<TCommonSynchronizationEnvironmentTypes>) => void;
export type ForEachFieldInfoOfDataCallbackOptions<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint> = FieldInfoOfData<TCommonSynchronizationEnvironmentTypes>;
export type FilterInfosCallback<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint> = (options: {
  fieldInfo: ObjectFieldInfo<TCommonSynchronizationEnvironmentTypes, any, any>;
}) => boolean;

/**
 * the object which contains the value
 * e.g. for a value path of ['innerObject', 'value'], the data will be the `innerObject`
 * for a value path of ['value'], the data will be the root object/entity
 */
export type DataOfValue = Record<string | number | symbol, any>;
export type FieldInfoWithPath<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint> = {
  path: FieldInfoPath;
  fieldInfo: ObjectFieldInfo<TCommonSynchronizationEnvironmentTypes, any, any>;
};
export type FieldInfoOfData<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint> = {
  path: FieldInfoDetailedPath;
  data: DataOfValue;
  value: any;
  fieldInfo: ObjectFieldInfo<TCommonSynchronizationEnvironmentTypes, any, any>;
};
type FilteredEntityInfo<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint> = {
  info: ObjectFieldInfo<TCommonSynchronizationEnvironmentTypes, any, any>;
  innerInfos: Array<FilteredEntityInfo<TCommonSynchronizationEnvironmentTypes>>;
  isArray: boolean;
  isHidden: boolean;
};
type ConstructorOptions<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint, TEntity> = {
  entityInfo: EntityInfo<TCommonSynchronizationEnvironmentTypes, any, TEntity>;
  fieldInfos: ObjectFieldInfos<TCommonSynchronizationEnvironmentTypes, any>;
  fieldInfoConfigurations: FieldInfoConfigurationsMap<TCommonSynchronizationEnvironmentTypes>;
};
type FieldInfoConfigurationsMap<TCommonSynchronizationEnvironmentTypes extends AnyCommonSynchronizationEnvironmentTypesConstraint> = Map<ObjectFieldInfo<TCommonSynchronizationEnvironmentTypes, any, any>, FieldInfoConfiguration>;
type FieldInfoConfiguration = {
  /**
   * hidden = true means that the field info is only here for internal usage.
   * It should not occur in e.g. forEachFieldInfo
   */
  hidden: boolean;
};